import { useState } from 'react';

interface CaseStudySubmissionModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const CaseStudySubmissionModal = ({ isOpen, onClose }: CaseStudySubmissionModalProps) => {
  const [formData, setFormData] = useState({
    companyName: '',
    contactName: '',
    email: '',
    phone: '',
    industry: '',
    projectTitle: '',
    challenge: '',
    solution: '',
    results: '',
    testimonial: '',
    projectDuration: '',
    technologies: '',
    websiteUrl: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new FormData();
      Object.entries(formData).forEach(([key, value]) => {
        formBody.append(key, value);
      });

      const supabaseUrl = import.meta.env.VITE_PUBLIC_SUPABASE_URL;
      const response = await fetch(`${supabaseUrl}/functions/v1/purecode-case-study-submission`, {
        method: 'POST',
        body: formBody
      });

      const result = await response.json();

      if (response.ok && result.success) {
        setSubmitStatus('success');
        setFormData({
          companyName: '',
          contactName: '',
          email: '',
          phone: '',
          industry: '',
          projectTitle: '',
          challenge: '',
          solution: '',
          results: '',
          testimonial: '',
          projectDuration: '',
          technologies: '',
          websiteUrl: ''
        });
        setTimeout(() => {
          onClose();
          setSubmitStatus('idle');
        }, 3000);
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      console.error('Submission error:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 transition-opacity bg-gray-900 bg-opacity-75" onClick={onClose}></div>

        <div className="inline-block w-full max-w-3xl my-8 overflow-hidden text-left align-middle transition-all transform bg-white rounded-xl shadow-2xl">
          <div className="relative">
            <div className="bg-gradient-to-r from-teal-600 to-cyan-600 px-6 py-6">
              <h3 className="text-2xl font-bold text-white">
                Share Your Success Story
              </h3>
              <p className="text-teal-100 mt-2">
                Tell us about your project and how we helped achieve your goals
              </p>
            </div>

            <button
              onClick={onClose}
              className="absolute top-4 right-4 text-white hover:text-gray-200 transition-colors cursor-pointer"
            >
              <i className="ri-close-line text-2xl"></i>
            </button>
          </div>

          <form id="case-study-form" data-readdy-form onSubmit={handleSubmit} className="px-6 py-6 max-h-[70vh] overflow-y-auto">
            {submitStatus === 'success' && (
              <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
                <div className="flex items-center">
                  <i className="ri-checkbox-circle-fill text-green-600 text-xl mr-3"></i>
                  <p className="text-green-800 font-medium">Thank you! Your case study submission has been received.</p>
                </div>
              </div>
            )}

            {submitStatus === 'error' && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                <div className="flex items-center">
                  <i className="ri-error-warning-fill text-red-600 text-xl mr-3"></i>
                  <p className="text-red-800 font-medium">Something went wrong. Please try again.</p>
                </div>
              </div>
            )}

            <div className="space-y-5">
              {/* Company Information */}
              <div className="border-b pb-4">
                <h4 className="text-lg font-semibold text-gray-900 mb-4">Company Information</h4>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="companyName" className="block text-sm font-medium text-gray-700 mb-1">
                      Company Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="companyName"
                      name="companyName"
                      value={formData.companyName}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                      placeholder="Your company name"
                    />
                  </div>

                  <div>
                    <label htmlFor="industry" className="block text-sm font-medium text-gray-700 mb-1">
                      Industry <span className="text-red-500">*</span>
                    </label>
                    <select
                      id="industry"
                      name="industry"
                      value={formData.industry}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent cursor-pointer"
                    >
                      <option value="">Select industry</option>
                      <option value="E-commerce">E-commerce</option>
                      <option value="Healthcare">Healthcare</option>
                      <option value="Finance">Finance</option>
                      <option value="Manufacturing">Manufacturing</option>
                      <option value="Education">Education</option>
                      <option value="Logistics">Logistics</option>
                      <option value="Real Estate">Real Estate</option>
                      <option value="Technology">Technology</option>
                      <option value="Retail">Retail</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>

                  <div>
                    <label htmlFor="contactName" className="block text-sm font-medium text-gray-700 mb-1">
                      Contact Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="contactName"
                      name="contactName"
                      value={formData.contactName}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                      placeholder="Your full name"
                    />
                  </div>

                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                      Email Address <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                      placeholder="your@email.com"
                    />
                  </div>

                  <div>
                    <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
                      Phone Number
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      className="w-full px-4 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                      placeholder="+44 123 456 7890"
                    />
                  </div>

                  <div>
                    <label htmlFor="websiteUrl" className="block text-sm font-medium text-gray-700 mb-1">
                      Company Website
                    </label>
                    <input
                      type="url"
                      id="websiteUrl"
                      name="websiteUrl"
                      value={formData.websiteUrl}
                      onChange={handleChange}
                      className="w-full px-4 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                      placeholder="https://yourcompany.com"
                    />
                  </div>
                </div>
              </div>

              {/* Project Details */}
              <div className="border-b pb-4">
                <h4 className="text-lg font-semibold text-gray-900 mb-4">Project Details</h4>
                
                <div className="space-y-4">
                  <div>
                    <label htmlFor="projectTitle" className="block text-sm font-medium text-gray-700 mb-1">
                      Project Title <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="projectTitle"
                      name="projectTitle"
                      value={formData.projectTitle}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                      placeholder="e.g., E-commerce Platform Transformation"
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="projectDuration" className="block text-sm font-medium text-gray-700 mb-1">
                        Project Duration
                      </label>
                      <input
                        type="text"
                        id="projectDuration"
                        name="projectDuration"
                        value={formData.projectDuration}
                        onChange={handleChange}
                        className="w-full px-4 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                        placeholder="e.g., 6 months"
                      />
                    </div>

                    <div>
                      <label htmlFor="technologies" className="block text-sm font-medium text-gray-700 mb-1">
                        Technologies Used
                      </label>
                      <input
                        type="text"
                        id="technologies"
                        name="technologies"
                        value={formData.technologies}
                        onChange={handleChange}
                        className="w-full px-4 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                        placeholder="e.g., React, Node.js, AWS"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="challenge" className="block text-sm font-medium text-gray-700 mb-1">
                      Challenge <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      id="challenge"
                      name="challenge"
                      value={formData.challenge}
                      onChange={handleChange}
                      required
                      rows={3}
                      maxLength={500}
                      className="w-full px-4 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent resize-none"
                      placeholder="Describe the business challenge or problem you faced..."
                    ></textarea>
                    <p className="text-xs text-gray-500 mt-1">{formData.challenge.length}/500 characters</p>
                  </div>

                  <div>
                    <label htmlFor="solution" className="block text-sm font-medium text-gray-700 mb-1">
                      Solution <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      id="solution"
                      name="solution"
                      value={formData.solution}
                      onChange={handleChange}
                      required
                      rows={3}
                      maxLength={500}
                      className="w-full px-4 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent resize-none"
                      placeholder="Describe the solution PureCode provided..."
                    ></textarea>
                    <p className="text-xs text-gray-500 mt-1">{formData.solution.length}/500 characters</p>
                  </div>

                  <div>
                    <label htmlFor="results" className="block text-sm font-medium text-gray-700 mb-1">
                      Results & Impact <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      id="results"
                      name="results"
                      value={formData.results}
                      onChange={handleChange}
                      required
                      rows={3}
                      maxLength={500}
                      className="w-full px-4 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent resize-none"
                      placeholder="Share measurable results, improvements, or business impact..."
                    ></textarea>
                    <p className="text-xs text-gray-500 mt-1">{formData.results.length}/500 characters</p>
                  </div>
                </div>
              </div>

              {/* Testimonial */}
              <div>
                <h4 className="text-lg font-semibold text-gray-900 mb-4">Testimonial</h4>
                
                <div>
                  <label htmlFor="testimonial" className="block text-sm font-medium text-gray-700 mb-1">
                    Your Testimonial <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    id="testimonial"
                    name="testimonial"
                    value={formData.testimonial}
                    onChange={handleChange}
                    required
                    rows={4}
                    maxLength={500}
                    className="w-full px-4 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent resize-none"
                    placeholder="Share your experience working with PureCode and the value delivered..."
                  ></textarea>
                  <p className="text-xs text-gray-500 mt-1">{formData.testimonial.length}/500 characters</p>
                </div>
              </div>
            </div>

            <div className="flex gap-3 mt-6 pt-6 border-t">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="flex-1 px-6 py-3 bg-gradient-to-r from-teal-600 to-cyan-600 text-white font-medium rounded-lg hover:from-teal-500 hover:to-cyan-500 transition-all disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
              >
                {isSubmitting ? (
                  <span className="flex items-center justify-center">
                    <i className="ri-loader-4-line animate-spin mr-2"></i>
                    Submitting...
                  </span>
                ) : (
                  'Submit Case Study'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default CaseStudySubmissionModal;
